/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import  MsDyn365,{ ICoreContext } from '@msdyn365-commerce/core';
import AccordionItem from 'mfcl/AccordionItem';
import { Node ,addThrottledEvent } from '@msdyn365-commerce-modules/utilities';
import _getViewPort from '../../Utilities/get-view-port';
import { IMfrmProductBenefitsData } from './mfrm-product-benefits.data';
import { IMfrmProductBenefitsProps } from './mfrm-product-benefits.props.autogenerated';
import getEnhancedContentAction, { GetProductBenefitsEnhancedContentInput } from '../../data-actions/get-product-benefit-enhanced-content.action';

export interface IMfrmProductBenefitsViewProps extends IMfrmProductBenefitsProps<IMfrmProductBenefitsData> {
    renderProductBenefitsAccordion: React.ReactNode;
}


export interface IMfrmProductBenefitsContent {
    label: string;
    icon: string;
    text: string;
    benefitContent:string | undefined;
    badge: JSX.Element | undefined;
}
export interface IMfrmProductBenefitsState {
    productBenefitEnhancedContent: any;
    isMobile: boolean;
    isTabletDesktop:boolean,
}
/**
 *
 * MfrmProductBenefits component
 * @extends {React.PureComponent<IMfrmProductBenefitsProps<IMfrmProductBenefitsData>>}
 */
class MfrmProductBenefits extends React.PureComponent<IMfrmProductBenefitsProps<IMfrmProductBenefitsData>, IMfrmProductBenefitsState> {
    private resizeThrottledEventHandler?: (event: Event) => void;
    constructor(props: IMfrmProductBenefitsProps<IMfrmProductBenefitsData>) {
        super(props);
        this.state = {
            productBenefitEnhancedContent: {},
            isMobile:false,
            isTabletDesktop:false
        };
}
    public render(): JSX.Element | null {
        const viewProps = {
            ...(this.props as IMfrmProductBenefitsProps<IMfrmProductBenefitsData>),
            renderProductBenefitsAccordion: this._renderProductBenefitsAccordion()
        };
        return this.props.renderView(viewProps);
    }
    private updateViewPort = (context: ICoreContext): void => {
        const viewport = _getViewPort(context);
        if (viewport === 'xs' || viewport === 'sm') {
            this.setState({
                isTabletDesktop: false,
                isMobile: true
            });
        } else {
            this.setState({
                isTabletDesktop: true,
                isMobile: false
            });
        }
    };
    public async componentDidMount(): Promise<void> {
        const masterProductId =this.props.data.product.result?.MasterProductId;
        if(masterProductId){
            await this._getEnhancedContent(masterProductId);
        }
        if (MsDyn365.isBrowser) {
            this.updateViewPort(this.props.context);
        }
    }
    public async componentDidUpdate(): Promise<void> {
       const masterProductId =this.props.data.product.result?.MasterProductId;
        if (masterProductId) {
            await this._getEnhancedContent(masterProductId);
        }
        if (MsDyn365.isBrowser) {
            this.resizeThrottledEventHandler = addThrottledEvent(window, 'resize', () => this.updateViewPort(this.props.context));
        }
    }
    public componentWillUnmount(): void {
        MsDyn365.isBrowser && window.removeEventListener('resize', this.resizeThrottledEventHandler!, false);
    }
    private _renderTitleBadge = (title: string, scoreBadge?: JSX.Element): JSX.Element | undefined => {
        return (
            <>
                <Node className='product-benefits__title'>{title || ''}</Node>
                {scoreBadge && scoreBadge}
            </>
        );
    };

    private _getBadges = (scoreValue: string | undefined): JSX.Element | undefined => {
        try {
            if (!scoreValue || scoreValue === '0') {
                return;
            }
            const { scoreBadges } = this.props.config;
            const badgeText = scoreBadges && scoreBadges.length > 0 && scoreBadges?.find((item) => (Number(scoreValue) >= item.minValue && Number(scoreValue) <= item.maxValue))?.text;
            if (!badgeText) {
                return;
            }
            return (
                <Node className='product-benefits__score'>
                    <Node className='product-benefits__score-text' tag='span'>
                        {scoreValue}/10 {badgeText}
                    </Node>
                </Node>
            );
        } catch (error) {
            console.log('error[_getBadges]', error);
            return;
        }
    };
    private _getEnhancedContent = async (masterProductId:number | undefined) => {
        const productBenefitInput = new GetProductBenefitsEnhancedContentInput(masterProductId ? masterProductId.toString() :'');
        const productBenefitData = await getEnhancedContentAction(productBenefitInput, this.props.context.actionContext);
        if(productBenefitData){
            this.setState({
                productBenefitEnhancedContent:productBenefitData
            });
        }
    };

    private _renderEnhancedContent = (description: string, enhancedContent?: string): JSX.Element | undefined => {
        return (
            <>
                <Node className='product-benefits__description' >{description || ''}</Node>
                <Node dangerouslySetInnerHTML={{ __html: enhancedContent }} className='product-benefits__enhanced-content' />
            </>
        );
    };

    private _renderProductBenefitsAccordion = (): JSX.Element | undefined => {
        const  productBenefitsData =this.props.data.productBenefitsData.result;
        const enhancedContentData=this.state.productBenefitEnhancedContent;
        const productBenefitsAccordionContent: IMfrmProductBenefitsContent[] = [];

        if (this.props.data.productSpecificationData.result && this.props.data.productSpecificationData.result !== undefined) {
            const attributes = this.props.data.productSpecificationData.result;
            const spotlightAttributeKey = attributes[attributes.findIndex(obj => obj.Name === 'PDP_Feature Spotlight_Sequence')];
            const spotlightValues = spotlightAttributeKey?.TextValue ? spotlightAttributeKey?.TextValue : undefined;
            if (spotlightValues !== undefined) {
                const spotlightValuesArr = spotlightValues && spotlightValues.split(',');
                spotlightValuesArr &&
                    spotlightValuesArr.length > 0 &&
                    spotlightValuesArr.map((item, index) => {
                        const attribute = attributes && attributes[attributes.findIndex(obj => obj.Name === item)];
                        if (attribute !== undefined && attribute.BooleanValue === true && productBenefitsAccordionContent.length < 13) {
                            const scoreAttribute = attributes && attributes[attributes.findIndex(obj => obj.Name?.toLowerCase() === `${attribute.Name} score`?.toLowerCase())];
                            const badge = scoreAttribute ? this._getBadges(scoreAttribute?.TextValue) : undefined;
                            const obj = {
                                label: item,
                                icon: productBenefitsData && productBenefitsData[item]?.icon ? productBenefitsData[item].icon : '' ,
                                text: productBenefitsData && productBenefitsData[item]?.description ? productBenefitsData[item].description : '' ,
                                benefitContent: enhancedContentData && enhancedContentData[item]?.htmlBlob ? enhancedContentData[item].htmlBlob :'',
                                badge
                            };
                            productBenefitsAccordionContent.push(obj);
                        }
                    });
            }
        }
        return (
            <>
                {productBenefitsAccordionContent && productBenefitsAccordionContent.length > 0 ? (
                    <Node className='mfrm-accordion-container mfrm-accordion__product-benefits'>
                        <Node tag='h2' className='mfrm-accordion__product-benefits-heading'>
                            {this.props.resources.productBenefitsHeading}
                        </Node>
                        <Node className='mfrm-product-benefits-accordion'>
                            <Node className='product-benefits'>
                                {
                                    productBenefitsAccordionContent.map((item: IMfrmProductBenefitsContent, index: number) => (
                                        <AccordionItem
                                            initialOpen={true}
                                            className={`${ this.state.isTabletDesktop && item.benefitContent ==='' ? 'product-benefits__disabled' : this.state.isMobile && item.text===''? 'product-benefits__disabled':''}`}
                                            key={index}
                                            title={this._renderTitleBadge(item.label, item.badge)}
                                            icon={<img src={item.icon} alt={`${item.label} icon image`} className='product-benefits__icon' />}
                                            preview={item.text}
                                            id={item.label || ''}
                                            content={this._renderEnhancedContent(item.text,item.benefitContent)}
                                            focused={0}
                                        />
                                    ))}
                            </Node>
                        </Node>
                    </Node>
                ) : null}
            </>
        );
    };
}

export default MfrmProductBenefits;
